/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { User } from '@models/user/user.ts';
import httpRequest from '@utils/http-request.ts';

export interface MicrosoftAuthorizeResponse {
    authorizationUrl: string;
    state: string;
    expiresAt: number;
    scopes: string[];
}

export interface MicrosoftMessageAddress {
    address: string;
    name?: string;
}

export interface MicrosoftRecipient {
    emailAddress: MicrosoftMessageAddress;
}

export interface MicrosoftMessage {
    id: string;
    subject: string;
    from?: MicrosoftRecipient | null;
    receivedDateTime: number;
    isRead: boolean;
    bodyPreview: string;
    webLink: string;
}

export interface MicrosoftListMessagesResult {
    messages: MicrosoftMessage[];
    nextLink?: string;
}

export interface MicrosoftMessagesQuery {
    top?: number;
    skip?: number;
    skipToken?: string;
    folderId?: string;
}

export interface MicrosoftMailPayload {
    subject: string;
    content: string;
    contentType?: 'Text' | 'HTML';
    to: string[];
    cc?: string[];
    bcc?: string[];
    replyTo?: string[];
    saveToSentItems?: boolean;
}

const API_BASE = `${import.meta.env.VITE_SERVER_ENDPOINT}/user/user/microsoft`;

const buildQueryString = (params?: MicrosoftMessagesQuery) => {
    const query = new URLSearchParams();
    if (params?.top !== undefined) {
        query.set('top', String(params.top));
    }
    if (params?.skip !== undefined) {
        query.set('skip', String(params.skip));
    }
    if (params?.skipToken) {
        query.set('skipToken', params.skipToken);
    }
    if (params?.folderId) {
        query.set('folderId', params.folderId);
    }
    const qs = query.toString();
    return qs ? `?${qs}` : '';
};

const mapRecipients = (addresses: string[] | undefined) => {
    if (!addresses || addresses.length === 0) {
        return undefined;
    }
    return addresses
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0)
        .map((address) => ({ address }));
};

export const beginMicrosoftBinding = async (): Promise<MicrosoftAuthorizeResponse> => {
    const response = await httpRequest(`${API_BASE}/authorize`, 'POST');
    const json = await response.json();
    if (response.ok) {
        localStorage.setItem('microsoft_oauth_mode', 'bind');
        return json.data as MicrosoftAuthorizeResponse;
    }
    throw new Error(json.message ?? 'Failed to start Microsoft authorization');
};

export const completeMicrosoftBinding = async (payload: { code: string; state: string }): Promise<User> => {
    const response = await httpRequest(`${API_BASE}/complete`, 'POST', payload);
    const json = await response.json();
    if (response.ok) {
        return User.fromJSON(json.data);
    }
    throw new Error(json.message ?? 'Failed to complete Microsoft binding');
};

export const disconnectMicrosoftAccount = async (): Promise<User> => {
    const response = await httpRequest(API_BASE, 'DELETE');
    const json = await response.json();
    if (response.ok) {
        return User.fromJSON(json.data);
    }
    throw new Error(json.message ?? 'Failed to disconnect Microsoft account');
};

export const beginMicrosoftLogin = async (): Promise<MicrosoftAuthorizeResponse> => {
    const response = await httpRequest(`${API_BASE}/login/authorize`, 'POST');
    const json = await response.json();
    if (response.ok) {
        localStorage.setItem('microsoft_oauth_mode', 'login');
        return json.data as MicrosoftAuthorizeResponse;
    }
    throw new Error(json.message ?? 'Failed to start Microsoft login');
};

export const completeMicrosoftLogin = async (payload: {
    code: string;
    state: string;
}): Promise<{ token: string; user: User }> => {
    const response = await httpRequest(`${API_BASE}/login/complete`, 'POST', payload);
    const json = await response.json();
    if (response.ok && json.data) {
        return {
            token: json.data.token as string,
            user: User.fromJSON(json.data.user),
        };
    }
    throw new Error(json.message ?? 'Failed to complete Microsoft login');
};

export const listMicrosoftMessages = async (params?: MicrosoftMessagesQuery): Promise<MicrosoftListMessagesResult> => {
    const response = await httpRequest(`${API_BASE}/messages${buildQueryString(params)}`, 'GET');
    const json = await response.json();
    if (response.ok) {
        const data = json.data ?? {};
        const messages = Array.isArray(data.messages)
            ? (data.messages as Array<{ receivedDateTime?: number }>).map((item) => ({
                  ...item,
                  receivedDateTime: Number(item.receivedDateTime ?? 0),
              }))
            : [];
        return {
            messages: messages as MicrosoftMessage[],
            nextLink: data.nextLink ?? undefined,
        };
    }
    throw new Error(json.message ?? 'Failed to fetch Microsoft messages');
};

export const sendMicrosoftMail = async (payload: MicrosoftMailPayload): Promise<void> => {
    const toRecipients = mapRecipients(payload.to);
    if (!toRecipients || toRecipients.length === 0) {
        throw new Error('At least one recipient is required');
    }

    const body = {
        subject: payload.subject,
        body: {
            contentType: payload.contentType ?? 'Text',
            content: payload.content,
        },
        to: toRecipients,
        cc: mapRecipients(payload.cc),
        bcc: mapRecipients(payload.bcc),
        replyTo: mapRecipients(payload.replyTo),
        saveToSentItems: payload.saveToSentItems,
    };

    const response = await httpRequest(`${API_BASE}/messages`, 'POST', body);
    if (!response.ok) {
        const json = await response.json();
        throw new Error(json.message ?? 'Failed to send Microsoft email');
    }
};
